/**
 * XML Import Module for OptimalAvailability Studio
 *
 * Parses XML files (including Isograph AWB format) and converts them to the app's
 * internal JSON format. Complements the XML export functionality in main.js.
 *
 * @module xmlImport
 */

/**
 * XMLImport class provides static methods for parsing and importing XML data
 * from various formats (AWB, generic XML, and OAWX proprietary format).
 */
export class XMLImport {
  /**
   * Parses an XML string into a DOM tree using the browser's DOMParser.
   *
   * @param {string} xmlString - Raw XML string to parse
   * @returns {Document} - Parsed XML DOM tree
   * @throws {Error} - If XML is malformed
   */
  static parseXML(xmlString) {
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlString, 'text/xml');

      // Check for parse errors
      if (xmlDoc.getElementsByTagName('parsererror').length > 0) {
        throw new Error('XML parsing failed: ' + xmlDoc.getElementsByTagName('parsererror')[0].textContent);
      }

      return xmlDoc;
    } catch (err) {
      throw new Error(`XML Parse Error: ${err.message}`);
    }
  }

  /**
   * Converts an XML DOM tree to a nested JSON object structure.
   * Recursively processes all elements and attributes.
   *
   * @param {Document|Element} xmlNode - XML node to convert
   * @returns {object} - Nested JSON representation
   */
  static xmlToJson(xmlNode) {
    const result = {};

    // Handle document node
    if (xmlNode.nodeType === 9) {
      const root = xmlNode.documentElement;
      return this.xmlToJson(root);
    }

    // Handle element nodes
    if (xmlNode.nodeType === 1) {
      const tagName = xmlNode.tagName.toLowerCase();
      const element = {};

      // Process attributes
      if (xmlNode.attributes.length > 0) {
        element._attributes = {};
        for (let i = 0; i < xmlNode.attributes.length; i++) {
          const attr = xmlNode.attributes[i];
          element._attributes[attr.name] = attr.value;
        }
      }

      // Process child nodes
      const children = {};
      let hasChildElements = false;

      for (let i = 0; i < xmlNode.childNodes.length; i++) {
        const child = xmlNode.childNodes[i];

        // Skip text nodes (unless they contain meaningful text)
        if (child.nodeType === 3) {
          const text = child.nodeValue.trim();
          if (text) {
            element._text = text;
          }
          continue;
        }

        // Process child elements
        if (child.nodeType === 1) {
          hasChildElements = true;
          const childName = child.tagName.toLowerCase();
          const childJson = this.xmlToJson(child);

          if (children[childName]) {
            if (!Array.isArray(children[childName])) {
              children[childName] = [children[childName]];
            }
            children[childName].push(childJson);
          } else {
            children[childName] = childJson;
          }
        }
      }

      // Merge children into element
      if (hasChildElements) {
        Object.assign(element, children);
      }

      result[tagName] = element;
      return result;
    }

    return result;
  }

  /**
   * Flattens nested XML JSON structure to flat key-value pairs.
   * Uses dot notation for nested paths (e.g., "location.equipment.name").
   *
   * @param {object} jsonObj - Nested JSON object
   * @param {string} prefix - Path prefix for flattening
   * @returns {object} - Flat key-value pairs
   */
  static flattenXml(jsonObj, prefix = '') {
    const result = {};

    const flatten = (obj, currentPrefix) => {
      for (const [key, value] of Object.entries(obj)) {
        if (key === '_attributes' || key === '_text') continue;

        const newKey = currentPrefix ? `${currentPrefix}.${key}` : key;

        if (value === null || value === undefined) {
          continue;
        } else if (Array.isArray(value)) {
          result[newKey] = value;
        } else if (typeof value === 'object' && !Array.isArray(value)) {
          flatten(value, newKey);
        } else {
          result[newKey] = value;
        }
      }
    };

    flatten(jsonObj, prefix);
    return result;
  }

  /**
   * Imports Isograph AWB XML format and maps it to internal format.
   * AWB is a hierarchical format with entities like Location, Equipment, FailureModel, etc.
   *
   * @param {string} xmlString - AWB XML content
   * @returns {object} - Internal project data format
   */
  static importAWBFile(xmlString) {
    const xmlDoc = this.parseXML(xmlString);
    const jsonData = this.xmlToJson(xmlDoc);

    // Extract root element (usually "AnalysisStudio" or similar)
    const root = Object.values(jsonData)[0];
    if (!root) throw new Error('Invalid AWB file structure');

    const projectData = {
      metadata: {
        source: 'AWB Import',
        importedAt: new Date().toISOString(),
      },
      locations: [],
      equipments: [],
      failuremodels: [],
      spares: [],
      events: [],
      faultTrees: [],
      warnings: [],
    };

    // Map AWB entity types to internal format
    const awbMappings = {
      Location: 'locations',
      Equipment: 'equipments',
      FailureModel: 'failuremodels',
      Spare: 'spares',
      Event: 'events',
      FaultTree: 'faultTrees',
    };

    // Process all elements recursively
    const processElement = (element, parentId = null) => {
      for (const [key, value] of Object.entries(element)) {
        if (key === '_attributes' || key === '_text') continue;

        const awbType = key.charAt(0).toUpperCase() + key.slice(1);
        const internalKey = awbMappings[awbType];

        if (internalKey) {
          const items = Array.isArray(value) ? value : [value];
          items.forEach(item => {
            const mapped = this.mapAWBEntity(awbType, item, parentId);
            projectData[internalKey].push(mapped);

            // Process nested children
            if (typeof item === 'object') {
              processElement(item, mapped.id);
            }
          });
        } else if (typeof value === 'object' && value !== null) {
          processElement(value, parentId);
        }
      }
    };

    processElement(root);

    // Validate and summarize
    projectData.summary = this.getImportSummary(projectData);

    return projectData;
  }

  /**
   * Maps a single AWB entity to internal format.
   * Handles type conversion and nested hierarchy assignment.
   *
   * @param {string} awbType - AWB entity type (e.g., "Location", "Equipment")
   * @param {object} awbData - AWB entity data
   * @param {string} parentId - Parent entity ID for hierarchy
   * @returns {object} - Mapped entity in internal format
   */
  static mapAWBEntity(awbType, awbData, parentId = null) {
    const attrs = awbData._attributes || {};

    const baseEntity = {
      id: attrs.id || `${awbType}_${Math.random().toString(36).substr(2, 9)}`,
      name: attrs.name || attrs.title || '',
      type: awbType.toLowerCase(),
      parentId: parentId,
    };

    // Type-specific mappings
    switch (awbType) {
      case 'Location':
        return {
          ...baseEntity,
          description: attrs.description || '',
          latitude: parseFloat(attrs.latitude) || null,
          longitude: parseFloat(attrs.longitude) || null,
          equipmentIds: [],
        };

      case 'Equipment':
        return {
          ...baseEntity,
          equipmentType: attrs.equipmentType || '',
          manufacturer: attrs.manufacturer || '',
          model: attrs.model || '',
          serialNumber: attrs.serialNumber || '',
          failureModelIds: [],
        };

      case 'FailureModel':
        return {
          ...baseEntity,
          failureRate: parseFloat(attrs.failureRate) || 0,
          repairRate: parseFloat(attrs.repairRate) || 0,
          failureRateUnit: attrs.failureRateUnit || 'per_1e6_hours',
          distribution: attrs.distribution || 'weibull',
          shape: parseFloat(attrs.shape) || 1,
          scale: parseFloat(attrs.scale) || 1,
        };

      case 'Spare':
        return {
          ...baseEntity,
          equipmentType: attrs.equipmentType || '',
          quantity: parseInt(attrs.quantity) || 1,
          cost: parseFloat(attrs.cost) || 0,
          leadTime: parseInt(attrs.leadTime) || 0,
        };

      case 'Event':
        return {
          ...baseEntity,
          probability: parseFloat(attrs.probability) || 0,
          consequences: attrs.consequences || '',
        };

      case 'FaultTree':
        return {
          ...baseEntity,
          topEventId: attrs.topEventId || '',
          gateType: attrs.gateType || 'AND',
          children: [],
        };

      
      // OAV-079 (final): Markov chain analysis
      case 'MarkovChain':
        return {
          ...baseEntity,
          numStates: parseInt(attrs.numStates) || 0,
          initialState: attrs.initialState || '',
          stateIds: [],
          transitionIds: [],
        };
      case 'MarkovState':
        return {
          ...baseEntity,
          chainId: parentId,
          stateType: attrs.stateType || 'operational',
          isAbsorbing: attrs.isAbsorbing === 'true',
          initialProbability: parseFloat(attrs.initialProbability) || 0,
        };
      case 'MarkovTransition':
        return {
          ...baseEntity,
          chainId: parentId,
          fromStateId: attrs.fromStateId || attrs.from || '',
          toStateId: attrs.toStateId || attrs.to || '',
          rate: parseFloat(attrs.rate) || 0,
          rateUnit: attrs.rateUnit || 'per_hour',
        };

      // OAV-079 (final): Reliability network — graph topology
      case 'ReliabilityNetwork':
        return {
          ...baseEntity,
          method: attrs.method || 'series-parallel',
          sourceNodeId: attrs.sourceNodeId || '',
          sinkNodeId: attrs.sinkNodeId || '',
          nodeIds: [],
          edgeIds: [],
        };
      case 'NetworkNode':
        return {
          ...baseEntity,
          networkId: parentId,
          nodeType: attrs.nodeType || 'block',
          reliability: parseFloat(attrs.reliability) || 1.0,
          equipmentRef: attrs.equipmentRef || null,
        };
      case 'NetworkEdge':
        return {
          ...baseEntity,
          networkId: parentId,
          fromNodeId: attrs.fromNodeId || attrs.from || '',
          toNodeId: attrs.toNodeId || attrs.to || '',
          weight: parseFloat(attrs.weight) || 1.0,
        };

      // OAV-079 (final): Minimal cut sets
      case 'CutSet':
      case 'MCS':
      case 'MinCutSet':
        return {
          ...baseEntity,
          basicEventIds: (attrs.basicEventIds || attrs.events || '').split(',').map(s => s.trim()).filter(Boolean),
          probability: parseFloat(attrs.probability) || 0,
          order: parseInt(attrs.order) || 0,
          treeRef: attrs.treeRef || parentId,
        };

      // OAV-079 (final): Warranty / support contracts
      case 'WarrantyClause':
        return {
          ...baseEntity,
          equipmentRef: attrs.equipmentRef || parentId,
          coverageMonths: parseInt(attrs.coverageMonths) || 12,
          coverageType: attrs.coverageType || 'parts-and-labour',
          excludedFailureModes: (attrs.excludedFailureModes || '').split(',').map(s => s.trim()).filter(Boolean),
          deductible: parseFloat(attrs.deductible) || 0,
        };
      case 'SupportContract':
        return {
          ...baseEntity,
          vendorName: attrs.vendorName || '',
          startDate: attrs.startDate || '',
          endDate: attrs.endDate || '',
          slaResponseHours: parseFloat(attrs.slaResponseHours) || 24,
          annualCost: parseFloat(attrs.annualCost) || 0,
          coverageScope: attrs.coverageScope || '',
        };

      // OAV-079 (final): Lifecycle cost modelling
      case 'CostDatabase':
        return {
          ...baseEntity,
          currency: attrs.currency || 'GBP',
          baseYear: parseInt(attrs.baseYear) || new Date().getFullYear(),
          discountRate: parseFloat(attrs.discountRate) || 0.05,
          escalationRate: parseFloat(attrs.escalationRate) || 0.03,
          inflationRate: parseFloat(attrs.inflationRate) || 0.02,
          costNodeIds: [],
        };
      case 'LCCModel':
        return {
          ...baseEntity,
          databaseId: parentId,
          projectLife: parseInt(attrs.projectLife) || 20,
          analysisStartYear: parseInt(attrs.analysisStartYear) || new Date().getFullYear(),
          phases: (attrs.phases || 'capex,opex,disposal').split(',').map(s => s.trim()),
        };
      case 'CostNode':
        return {
          ...baseEntity,
          databaseId: parentId,
          category: attrs.category || 'opex',
          cost: parseFloat(attrs.cost) || 0,
          timing: parseInt(attrs.timing) || 0,
          escalation: parseFloat(attrs.escalation) || null,
          equipmentRef: attrs.equipmentRef || null,
        };

      
      // OAV-079 (final): Markov chain analysis
      case 'MarkovChain':
        return {
          ...baseEntity,
          numStates: parseInt(attrs.numStates) || 0,
          initialState: attrs.initialState || '',
          stateIds: [],
          transitionIds: [],
        };
      case 'MarkovState':
        return {
          ...baseEntity,
          chainId: parentId,
          stateType: attrs.stateType || 'operational',
          isAbsorbing: attrs.isAbsorbing === 'true',
          initialProbability: parseFloat(attrs.initialProbability) || 0,
        };
      case 'MarkovTransition':
        return {
          ...baseEntity,
          chainId: parentId,
          fromStateId: attrs.fromStateId || attrs.from || '',
          toStateId: attrs.toStateId || attrs.to || '',
          rate: parseFloat(attrs.rate) || 0,
          rateUnit: attrs.rateUnit || 'per_hour',
        };

      // OAV-079 (final): Reliability network — graph topology
      case 'ReliabilityNetwork':
        return {
          ...baseEntity,
          method: attrs.method || 'series-parallel',
          sourceNodeId: attrs.sourceNodeId || '',
          sinkNodeId: attrs.sinkNodeId || '',
          nodeIds: [],
          edgeIds: [],
        };
      case 'NetworkNode':
        return {
          ...baseEntity,
          networkId: parentId,
          nodeType: attrs.nodeType || 'block',
          reliability: parseFloat(attrs.reliability) || 1.0,
          equipmentRef: attrs.equipmentRef || null,
        };
      case 'NetworkEdge':
        return {
          ...baseEntity,
          networkId: parentId,
          fromNodeId: attrs.fromNodeId || attrs.from || '',
          toNodeId: attrs.toNodeId || attrs.to || '',
          weight: parseFloat(attrs.weight) || 1.0,
        };

      // OAV-079 (final): Minimal cut sets
      case 'CutSet':
      case 'MCS':
      case 'MinCutSet':
        return {
          ...baseEntity,
          basicEventIds: (attrs.basicEventIds || attrs.events || '').split(',').map(s => s.trim()).filter(Boolean),
          probability: parseFloat(attrs.probability) || 0,
          order: parseInt(attrs.order) || 0,
          treeRef: attrs.treeRef || parentId,
        };

      // OAV-079 (final): Warranty / support contracts
      case 'WarrantyClause':
        return {
          ...baseEntity,
          equipmentRef: attrs.equipmentRef || parentId,
          coverageMonths: parseInt(attrs.coverageMonths) || 12,
          coverageType: attrs.coverageType || 'parts-and-labour',
          excludedFailureModes: (attrs.excludedFailureModes || '').split(',').map(s => s.trim()).filter(Boolean),
          deductible: parseFloat(attrs.deductible) || 0,
        };
      case 'SupportContract':
        return {
          ...baseEntity,
          vendorName: attrs.vendorName || '',
          startDate: attrs.startDate || '',
          endDate: attrs.endDate || '',
          slaResponseHours: parseFloat(attrs.slaResponseHours) || 24,
          annualCost: parseFloat(attrs.annualCost) || 0,
          coverageScope: attrs.coverageScope || '',
        };

      // OAV-079 (final): Lifecycle cost modelling
      case 'CostDatabase':
        return {
          ...baseEntity,
          currency: attrs.currency || 'GBP',
          baseYear: parseInt(attrs.baseYear) || new Date().getFullYear(),
          discountRate: parseFloat(attrs.discountRate) || 0.05,
          escalationRate: parseFloat(attrs.escalationRate) || 0.03,
          inflationRate: parseFloat(attrs.inflationRate) || 0.02,
          costNodeIds: [],
        };
      case 'LCCModel':
        return {
          ...baseEntity,
          databaseId: parentId,
          projectLife: parseInt(attrs.projectLife) || 20,
          analysisStartYear: parseInt(attrs.analysisStartYear) || new Date().getFullYear(),
          phases: (attrs.phases || 'capex,opex,disposal').split(',').map(s => s.trim()),
        };
      case 'CostNode':
        return {
          ...baseEntity,
          databaseId: parentId,
          category: attrs.category || 'opex',
          cost: parseFloat(attrs.cost) || 0,
          timing: parseInt(attrs.timing) || 0,
          escalation: parseFloat(attrs.escalation) || null,
          equipmentRef: attrs.equipmentRef || null,
        };

      default:
        return baseEntity;
    }
  }

  /**
   * Imports any structured XML with auto-detection of entity types.
   * Attempts to intelligently map XML structure to internal format.
   *
   * @param {string} xmlString - Generic XML content
   * @returns {object} - Internal project data format
   */
  static importGenericXML(xmlString) {
    const xmlDoc = this.parseXML(xmlString);
    const jsonData = this.xmlToJson(xmlDoc);

    const projectData = {
      metadata: {
        source: 'Generic XML Import',
        importedAt: new Date().toISOString(),
      },
      locations: [],
      equipments: [],
      failuremodels: [],
      spares: [],
      events: [],
      faultTrees: [],
      warnings: [],
    };

    // Recursively detect and import entities
    const processNodes = (obj, parentId = null) => {
      for (const [key, value] of Object.entries(obj)) {
        if (key === '_attributes' || key === '_text') continue;

        const detectedType = this.detectEntityType(key, value._attributes || {});

        if (detectedType) {
          const items = Array.isArray(value) ? value : [value];
          items.forEach((item, idx) => {
            const mapped = {
              id: item._attributes?.id || `${detectedType}_${idx}`,
              name: item._attributes?.name || item._text || key,
              type: detectedType,
              parentId: parentId,
              ...item._attributes,
            };

            const targetKey = `${detectedType}s`;
            if (projectData[targetKey]) {
              projectData[targetKey].push(mapped);
            }

            if (typeof item === 'object') {
              processNodes(item, mapped.id);
            }
          });
        } else if (typeof value === 'object' && value !== null) {
          processNodes(value, parentId);
        }
      }
    };

    const root = Object.values(jsonData)[0];
    if (root) processNodes(root);

    projectData.summary = this.getImportSummary(projectData);
    return projectData;
  }

  /**
   * Detects entity type from XML element name and attributes.
   * Uses fuzzy matching to identify entity types.
   *
   * @param {string} elementName - XML element name
   * @param {object} attributes - Element attributes
   * @returns {string|null} - Detected entity type or null
   */
  static detectEntityType(elementName, attributes = {}) {
    const normalized = elementName.toLowerCase();

    // Direct mappings
    const typeMap = {
      location: 'location',
      equipment: 'equipment',
      failuremodel: 'failuremodel',
      'failure-model': 'failuremodel',
      failurerate: 'failuremodel',
      spare: 'spare',
      sparepart: 'spare',
      event: 'event',
      faulttree: 'faulttree',
      'fault-tree': 'faulttree',
      gate: 'faulttree',
      // OAV-079 (final) — extended Isograph elements
      markovchain: 'markovchain',
      'markov-chain': 'markovchain',
      markovstate: 'markovstate',
      'markov-state': 'markovstate',
      markovtransition: 'markovtransition',
      'markov-transition': 'markovtransition',
      reliabilitynetwork: 'reliabilitynetwork',
      'reliability-network': 'reliabilitynetwork',
      networknode: 'networknode',
      'network-node': 'networknode',
      networkedge: 'networkedge',
      'network-edge': 'networkedge',
      cutset: 'cutset',
      'cut-set': 'cutset',
      mcs: 'cutset',
      mincutset: 'cutset',
      'min-cut-set': 'cutset',
      warrantyclause: 'warrantyclause',
      'warranty-clause': 'warrantyclause',
      supportcontract: 'supportcontract',
      'support-contract': 'supportcontract',
      costdatabase: 'costdatabase',
      'cost-database': 'costdatabase',
      lccmodel: 'lccmodel',
      'lcc-model': 'lccmodel',
      costnode: 'costnode',
      'cost-node': 'costnode',
      // OAV-079 (final) — extended Isograph elements
      markovchain: 'markovchain',
      'markov-chain': 'markovchain',
      markovstate: 'markovstate',
      'markov-state': 'markovstate',
      markovtransition: 'markovtransition',
      'markov-transition': 'markovtransition',
      reliabilitynetwork: 'reliabilitynetwork',
      'reliability-network': 'reliabilitynetwork',
      networknode: 'networknode',
      'network-node': 'networknode',
      networkedge: 'networkedge',
      'network-edge': 'networkedge',
      cutset: 'cutset',
      'cut-set': 'cutset',
      mcs: 'cutset',
      mincutset: 'cutset',
      'min-cut-set': 'cutset',
      warrantyclause: 'warrantyclause',
      'warranty-clause': 'warrantyclause',
      supportcontract: 'supportcontract',
      'support-contract': 'supportcontract',
      costdatabase: 'costdatabase',
      'cost-database': 'costdatabase',
      lccmodel: 'lccmodel',
      'lcc-model': 'lccmodel',
      costnode: 'costnode',
      'cost-node': 'costnode',
    };

    // Fuzzy matching for partial names
    const fuzzyMap = {
      site: 'location',
      plant: 'location',
      system: 'equipment',
      device: 'equipment',
      component: 'equipment',
      failure: 'failuremodel',
      rate: 'failuremodel',
      inventory: 'spare',
      part: 'spare',
    };

    return typeMap[normalized] || fuzzyMap[normalized] || null;
  }

  /**
   * Auto-maps field names from source to target using fuzzy matching.
   * Useful for handling variations in XML field naming conventions.
   *
   * @param {string[]} sourceFields - Source field names
   * @param {string[]} targetFields - Target field names
   * @returns {object} - Field mapping (source -> target)
   */
  static autoMapFields(sourceFields, targetFields) {
    const mapping = {};

    sourceFields.forEach(source => {
      const normalized = source.toLowerCase().replace(/[_\-\s]/g, '');

      let bestMatch = null;
      let bestScore = 0;

      targetFields.forEach(target => {
        const targetNorm = target.toLowerCase().replace(/[_\-\s]/g, '');

        // Levenshtein-like scoring
        const score = this._stringSimilarity(normalized, targetNorm);

        if (score > bestScore) {
          bestScore = score;
          bestMatch = target;
        }
      });

      if (bestScore > 0.6) {
        mapping[source] = bestMatch;
      }
    });

    return mapping;
  }

  /**
   * Simple string similarity metric (0-1 scale).
   * Used for fuzzy field matching.
   *
   * @private
   * @param {string} str1 - First string
   * @param {string} str2 - Second string
   * @returns {number} - Similarity score
   */
  static _stringSimilarity(str1, str2) {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;

    if (longer.length === 0) return 1.0;

    const editDistance = this._levenshteinDistance(longer, shorter);
    return (longer.length - editDistance) / longer.length;
  }

  /**
   * Calculates Levenshtein distance between two strings.
   *
   * @private
   * @param {string} str1 - First string
   * @param {string} str2 - Second string
   * @returns {number} - Edit distance
   */
  static _levenshteinDistance(str1, str2) {
    const costs = [];

    for (let i = 0; i <= str1.length; i++) {
      let lastValue = i;
      for (let j = 0; j <= str2.length; j++) {
        if (i === 0) {
          costs[j] = j;
        } else if (j > 0) {
          let newValue = costs[j - 1];
          if (str1.charAt(i - 1) !== str2.charAt(j - 1)) {
            newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
          }
          costs[j - 1] = lastValue;
          lastValue = newValue;
        }
      }
      if (i > 0) costs[str2.length] = lastValue;
    }

    return costs[str2.length];
  }

  /**
   * Validates imported data structure for consistency and completeness.
   * Checks for required fields and data integrity.
   *
   * @param {object} data - Imported project data
   * @returns {object} - Validation result {isValid, errors, warnings}
   */
  static validateImport(data) {
    const errors = [];
    const warnings = [];

    // Check structure
    if (!data || typeof data !== 'object') {
      errors.push('Invalid data structure');
      return { isValid: false, errors, warnings };
    }

    // Check entities
    ['locations', 'equipments', 'failuremodels', 'spares'].forEach(key => {
      if (!Array.isArray(data[key])) {
        warnings.push(`Missing or invalid ${key} array`);
      }
    });

    // Validate locations
    (data.locations || []).forEach((loc, idx) => {
      if (!loc.id) errors.push(`Location ${idx}: missing id`);
      if (!loc.name) warnings.push(`Location ${idx}: missing name`);
    });

    // Validate equipments
    (data.equipments || []).forEach((eq, idx) => {
      if (!eq.id) errors.push(`Equipment ${idx}: missing id`);
      if (!eq.name) warnings.push(`Equipment ${idx}: missing name`);
      if (eq.parentId && !data.locations?.some(l => l.id === eq.parentId)) {
        warnings.push(`Equipment ${eq.id}: parent location not found`);
      }
    });

    // Validate failure models
    (data.failuremodels || []).forEach((fm, idx) => {
      if (!fm.id) errors.push(`FailureModel ${idx}: missing id`);
      if (fm.failureRate < 0) errors.push(`FailureModel ${fm.id}: negative failure rate`);
    });

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }

  /**
   * Generates a summary of imported data.
   * Counts entities and identifies any issues.
   *
   * @param {object} data - Imported project data
   * @returns {object} - Summary with entity counts, warnings, errors
   */
  static getImportSummary(data) {
    return {
      entityCounts: {
        locations: (data.locations || []).length,
        equipments: (data.equipments || []).length,
        failuremodels: (data.failuremodels || []).length,
        spares: (data.spares || []).length,
        events: (data.events || []).length,
        faultTrees: (data.faultTrees || []).length,
      },
      totalEntities: [
        ...(data.locations || []),
        ...(data.equipments || []),
        ...(data.failuremodels || []),
        ...(data.spares || []),
        ...(data.events || []),
        ...(data.faultTrees || []),
      ].length,
      warnings: data.warnings || [],
      errors: data.errors || [],
      importedAt: data.metadata?.importedAt || new Date().toISOString(),
    };
  }

  /**
   * Imports the app's own XML export format (.oawx).
   * OAWX is the proprietary Optimal Availability Studio XML format.
   *
   * @param {string} xmlString - OAWX XML content
   * @returns {object} - Project data in internal format
   */
  static importOAWX(xmlString) {
    const xmlDoc = this.parseXML(xmlString);
    const jsonData = this.xmlToJson(xmlDoc);

    // OAWX has structure: <oawx><project><locations>...</locations>...</project></oawx>
    const oawx = jsonData.oawx || Object.values(jsonData)[0];
    const project = oawx?.project || {};

    const projectData = {
      metadata: {
        source: 'OAWX Import',
        importedAt: new Date().toISOString(),
        name: project._attributes?.name || 'Untitled',
        version: project._attributes?.version || '1.0',
      },
      locations: this._parseOAWXArray(project.locations),
      equipments: this._parseOAWXArray(project.equipments),
      failuremodels: this._parseOAWXArray(project.failuremodels),
      spares: this._parseOAWXArray(project.spares),
      events: this._parseOAWXArray(project.events),
      faultTrees: this._parseOAWXArray(project.faulttrees),
      warnings: [],
    };

    projectData.summary = this.getImportSummary(projectData);
    return projectData;
  }

  /**
   * Exports project data to OAWX XML format.
   * Complement to importOAWX for round-trip serialization.
   *
   * @param {object} projectData - Project data in internal format
   * @returns {string} - OAWX XML string
   */
  static exportOAWX(projectData) {
    const metadata = projectData.metadata || {};

    let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
    xml += '<oawx version="1.0">\n';
    xml += `  <project name="${this._escapeXML(metadata.name || 'Untitled')}" version="${metadata.version || '1.0'}">\n`;

    // Export locations
    if (projectData.locations?.length > 0) {
      xml += '    <locations>\n';
      projectData.locations.forEach(loc => {
        xml += this._entityToXML(loc, 'location', 6);
      });
      xml += '    </locations>\n';
    }

    // Export equipments
    if (projectData.equipments?.length > 0) {
      xml += '    <equipments>\n';
      projectData.equipments.forEach(eq => {
        xml += this._entityToXML(eq, 'equipment', 6);
      });
      xml += '    </equipments>\n';
    }

    // Export failure models
    if (projectData.failuremodels?.length > 0) {
      xml += '    <failuremodels>\n';
      projectData.failuremodels.forEach(fm => {
        xml += this._entityToXML(fm, 'failuremodel', 6);
      });
      xml += '    </failuremodels>\n';
    }

    // Export spares
    if (projectData.spares?.length > 0) {
      xml += '    <spares>\n';
      projectData.spares.forEach(spare => {
        xml += this._entityToXML(spare, 'spare', 6);
      });
      xml += '    </spares>\n';
    }

    xml += '  </project>\n';
    xml += '</oawx>\n';

    return xml;
  }

  /**
   * Helper to parse OAWX array elements.
   * @private
   */
  static _parseOAWXArray(container) {
    if (!container) return [];

    const items = container.location || container.equipment || container.failuremodel ||
                  container.spare || container.event || container.faulttree || [];

    return Array.isArray(items) ? items.map(item => ({
      ...item._attributes,
      _text: item._text,
    })) : [{ ...items._attributes, _text: items._text }];
  }

  /**
   * Helper to convert entity to XML string.
   * @private
   */
  static _entityToXML(entity, tagName, indentLevel) {
    const indent = ' '.repeat(indentLevel);
    let xml = `${indent}<${tagName}`;

    for (const [key, value] of Object.entries(entity)) {
      if (key === '_text' || key === 'type' || key === 'parentId') continue;
      if (value === null || value === undefined) continue;
      xml += ` ${key}="${this._escapeXML(String(value))}"`;
    }

    xml += ' />\n';
    return xml;
  }

  /**
   * Helper to escape XML special characters.
   * @private
   */
  static _escapeXML(str) {
    return str.replace(/[<>&"']/g, ch => ({
      '<': '&lt;',
      '>': '&gt;',
      '&': '&amp;',
      '"': '&quot;',
      "'": '&apos;',
    }[ch]));
  }
}
